import { TrustiiApiClient, WalletSyncBlobResponse } from "../api/client.js";
import { afterSuccessfulPush, hydrateCrdtFromServer } from "../bridge/serverPersonas.js";
import { crdtMerge, CrdtState, emptyCrdtState } from "../crdt/merge.js";
import { incrementGCounter } from "../crdt/gCounter.js";
import { isDeviceRevoked } from "../crdt/orMap.js";
import { stampRegister } from "../crdt/lwwRegister.js";
import {
  deriveSyncKey,
  DeviceKeys,
  generateAccountRootKey,
  generateDeviceKeys,
  selfWrapSyncKey,
  devicePublicKeyJwk,
  unwrapSyncKeyFromJwk,
} from "../crypto/keys.js";
import {
  decryptRecoveryBackup,
  restoreSyncKeyFromBackup,
} from "../recovery/backup.js";
import { decryptEnvelope, encryptEnvelope, EncryptedBlob } from "../protocol/envelope.js";
import { verifyEnrollmentProof } from "../enrollment/proof.js";
import { withConflictRetry } from "./conflictRetry.js";

export class EnrollmentRequiredError extends Error {
  constructor(message = "enrollment_required") {
    super(message);
    this.name = "EnrollmentRequiredError";
  }
}

export class DeviceRevokedError extends Error {
  constructor(message = "device_revoked") {
    super(message);
    this.name = "DeviceRevokedError";
  }
}

export interface SecureStorageAdapter {
  get(key: string): Promise<Uint8Array | null>;
  set(key: string, value: Uint8Array): Promise<void>;
  delete(key: string): Promise<void>;
}

export interface AuthTokenProvider {
  (): Promise<string>;
}

export interface StepUpProvider {
  (): Promise<string>;
}

export interface EnrollmentTransport {
  displayQR?(payload: string): Promise<void>;
  scanQR?(): Promise<string>;
  openDeepLink?(url: string): Promise<void>;
}

export interface SyncSchedulerHooks {
  schedulePull?: () => void;
  schedulePush?: () => void;
}

export interface TrustiiWalletConfig {
  baseUrl: string;
  getAccessToken: AuthTokenProvider;
  accountId?: number;
}

export type WalletSyncEventMap = {
  syncComplete: { version: number };
  conflictResolved: { attempts: number };
  enrollmentRequired: Record<string, never>;
};

type EventHandler<K extends keyof WalletSyncEventMap> = (
  payload: WalletSyncEventMap[K],
) => void;

export interface WalletSyncEngineOptions {
  storage: SecureStorageAdapter;
  config: TrustiiWalletConfig;
  webauthnId?: string;
  stepUp?: StepUpProvider;
  enrollmentTransport?: EnrollmentTransport;
}

const STORAGE_KEYS = {
  accountRootKey: "trustii.accountRootKey",
  deviceKeys: "trustii.deviceKeys",
  syncKey: "trustii.syncKey",
  crdtState: "trustii.crdtState",
  lastVersion: "trustii.lastVersion",
};

let globalConfig: TrustiiWalletConfig | null = null;

export const TrustiiWallet = {
  configure(config: TrustiiWalletConfig): void {
    globalConfig = config;
  },
  getConfig(): TrustiiWalletConfig {
    if (!globalConfig) {
      throw new Error("trustii_wallet_not_configured");
    }
    return globalConfig;
  },
};

export class WalletSyncEngine {
  private readonly api: TrustiiApiClient;
  private accountRootKey: Uint8Array | null = null;
  private syncKey: Uint8Array | null = null;
  private deviceKeys: DeviceKeys | null = null;
  private crdtState: CrdtState = emptyCrdtState();
  private lastKnownVersion = 0;
  private localDirty = false;
  private personasChanged = false;
  private lastPulledVersion = 0;
  private pendingEnrollmentProof: string | null = null;
  private scheduler: SyncSchedulerHooks | null = null;
  private readonly listeners = new Map<string, Set<EventHandler<keyof WalletSyncEventMap>>>();

  constructor(private readonly options: WalletSyncEngineOptions) {
    this.api = new TrustiiApiClient(options.config);
  }

  getApiClient(): TrustiiApiClient {
    return this.api;
  }

  getWebauthnId(): string | undefined {
    return this.options.webauthnId;
  }

  getStepUpProvider(): StepUpProvider | undefined {
    return this.options.stepUp;
  }

  getEnrollmentTransport(): EnrollmentTransport | undefined {
    return this.options.enrollmentTransport;
  }

  attachScheduler(scheduler: SyncSchedulerHooks): void {
    this.scheduler = scheduler;
  }

  detachScheduler(): void {
    this.scheduler = null;
  }

  on<K extends keyof WalletSyncEventMap>(
    event: K,
    handler: EventHandler<K>,
  ): void {
    const handlers = this.listeners.get(event) ?? new Set();
    handlers.add(handler as EventHandler<keyof WalletSyncEventMap>);
    this.listeners.set(event, handlers);
  }

  private emit<K extends keyof WalletSyncEventMap>(
    event: K,
    payload: WalletSyncEventMap[K],
  ): void {
    for (const handler of this.listeners.get(event) ?? []) {
      (handler as EventHandler<K>)(payload);
    }
  }

  async initialize(): Promise<void> {
    const { storage } = this.options;
    const accountRootKey = await storage.get(STORAGE_KEYS.accountRootKey);
    const deviceKeysRaw = await storage.get(STORAGE_KEYS.deviceKeys);
    const syncKey = await storage.get(STORAGE_KEYS.syncKey);
    const crdtRaw = await storage.get(STORAGE_KEYS.crdtState);
    const lastVersionRaw = await storage.get(STORAGE_KEYS.lastVersion);

    if (deviceKeysRaw) {
      this.deviceKeys = JSON.parse(new TextDecoder().decode(deviceKeysRaw)) as DeviceKeys;
      this.deviceKeys = deserializeDeviceKeys(this.deviceKeys);
    }
    if (accountRootKey) this.accountRootKey = accountRootKey;
    if (syncKey) this.syncKey = syncKey;
    if (crdtRaw) {
      this.crdtState = JSON.parse(new TextDecoder().decode(crdtRaw)) as CrdtState;
    }
    if (lastVersionRaw) {
      this.lastKnownVersion = Number(new TextDecoder().decode(lastVersionRaw));
    }
    this.localDirty = false;
    this.lastPulledVersion = this.lastKnownVersion;
  }

  async bootstrap(accountId: number, options?: { hydratePersonas?: boolean }): Promise<void> {
    this.accountRootKey = generateAccountRootKey();
    this.deviceKeys = generateDeviceKeys();
    this.syncKey = deriveSyncKey(this.accountRootKey, accountId);
    this.crdtState = emptyCrdtState();

    const counter = incrementGCounter(this.crdtState.clock, this.deviceKeys.syncDeviceId);
    const deviceId = this.deviceKeys.syncDeviceId;
    const ts = { counter, device_id: deviceId };
    this.crdtState.devices[deviceId] = {
      device_id: stampRegister(deviceId, counter, deviceId),
      label: stampRegister("Primary device", counter, deviceId),
      public_key_jwk: stampRegister(devicePublicKeyJwk(this.deviceKeys), counter, deviceId),
      signing_public_key: stampRegister(
        Buffer.from(this.deviceKeys.signingPublicKey).toString("base64"),
        counter,
        deviceId,
      ),
      wrapped_sync_key: stampRegister(selfWrapSyncKey(this.syncKey, this.deviceKeys), counter, deviceId),
      enrolled_at: stampRegister(new Date().toISOString(), counter, deviceId),
    };

    if (options?.hydratePersonas !== false) {
      await hydrateCrdtFromServer(this.api, this);
    }

    this.localDirty = true;
    await this.persistLocal();
  }

  getLocalState(): CrdtState {
    return this.crdtState;
  }

  getDeviceKeys(): DeviceKeys | null {
    return this.deviceKeys;
  }

  getSyncKey(): Uint8Array | null {
    return this.syncKey;
  }

  getAccountRootKey(): Uint8Array | null {
    return this.accountRootKey;
  }

  getLastKnownVersion(): number {
    return this.lastKnownVersion;
  }

  isLocalDirty(): boolean {
    return this.localDirty;
  }

  localMutate(mutator: (state: CrdtState) => void, options?: { personasChanged?: boolean }): void {
    if (!this.deviceKeys) {
      throw new Error("wallet_not_initialized");
    }
    mutator(this.crdtState);
    incrementGCounter(this.crdtState.clock, this.deviceKeys.syncDeviceId);
    this.localDirty = true;
    if (options?.personasChanged) {
      this.personasChanged = true;
    }
    this.scheduler?.schedulePush?.();
  }

  async sync(): Promise<void> {
    await this.pull();
    if (this.localDirty) {
      await this.push();
    }
  }

  async pull(options?: { enrollmentProof?: string }): Promise<void> {
    if (options?.enrollmentProof) {
      this.pendingEnrollmentProof = options.enrollmentProof;
    }

    const remote = await this.api.getWalletSync();
    if (!remote.ciphertext_base64) {
      this.lastKnownVersion = remote.version ?? 0;
      this.lastPulledVersion = this.lastKnownVersion;
      if (this.lastKnownVersion === 0 && Object.keys(this.crdtState.devices).length === 0) {
        this.emit("enrollmentRequired", {});
        throw new EnrollmentRequiredError();
      }
      if (!this.syncKey) {
        this.emit("enrollmentRequired", {});
        throw new EnrollmentRequiredError();
      }
      await this.persistLocal();
      return;
    }

    this.ensureDeviceKeys();

    if (!this.syncKey) {
      const entry = this.crdtState.devices[this.deviceKeys!.syncDeviceId];
      const wrapped = entry?.wrapped_sync_key?.value;
      if (typeof wrapped !== "string") {
        this.emit("enrollmentRequired", {});
        throw new EnrollmentRequiredError();
      }
    }

    await this.ensureSyncKey(remote);
    const envelope = decryptEnvelope(this.syncKey!, remote as EncryptedBlob);
    this.crdtState = crdtMerge(this.crdtState, envelope.crdt_state);
    this.lastKnownVersion = remote.version ?? 0;
    this.lastPulledVersion = this.lastKnownVersion;
    this.localDirty = false;

    if (this.pendingEnrollmentProof) {
      this.verifyEnrollmentProofFromCrdt(this.pendingEnrollmentProof);
      this.pendingEnrollmentProof = null;
    }

    if (this.isLocalDeviceRevoked()) {
      throw new DeviceRevokedError();
    }

    await this.persistLocal();
  }

  async push(): Promise<void> {
    this.ensureInitialized();
    const deviceId = this.deviceKeys!.syncDeviceId;

    const { result, attempts } = await withConflictRetry(async () => {
      const blob = encryptEnvelope(this.syncKey!, this.crdtState, deviceId);
      const response = await this.api.putWalletSync(blob, this.lastKnownVersion || undefined);

      if ("error" in response && response.error === "version_conflict") {
        const conflict = response;
        if (conflict.current_blob?.ciphertext_base64) {
          const envelope = decryptEnvelope(this.syncKey!, conflict.current_blob as EncryptedBlob);
          this.crdtState = crdtMerge(this.crdtState, envelope.crdt_state);
          this.lastKnownVersion = conflict.current_version;
          await this.persistLocal();
        }
        throw new Error("version_conflict");
      }

      const success = response as { version: number };
      this.lastKnownVersion = success.version;
      this.localDirty = false;
      await this.persistLocal();
      return success.version;
    });

    if (attempts > 1) {
      this.emit("conflictResolved", { attempts });
    }

    if (this.personasChanged) {
      await afterSuccessfulPush(this.api, this, true);
      this.personasChanged = false;
    }

    this.emit("syncComplete", { version: result });
  }

  async revokeDevice(targetSyncDeviceId: string, label?: string): Promise<void> {
    this.localMutate((state) => {
      const deviceId = this.deviceKeys!.syncDeviceId;
      const counter = incrementGCounter(state.clock, deviceId);
      const entry = state.devices[targetSyncDeviceId];
      if (!entry) {
        throw new Error("device_not_found");
      }
      entry.revoked_at = stampRegister(new Date().toISOString(), counter, deviceId);
      if (label) {
        entry.label = stampRegister(label, counter, deviceId);
      }
    });
    await this.push();
  }

  isLocalDeviceRevoked(): boolean {
    if (!this.deviceKeys) {
      return false;
    }
    return isDeviceRevoked(this.crdtState.devices[this.deviceKeys.syncDeviceId]);
  }

  unwrapSyncKeyFromCrdt(): Uint8Array {
    this.ensureDeviceKeys();
    const entry = this.crdtState.devices[this.deviceKeys!.syncDeviceId];
    const wrapped = entry?.wrapped_sync_key?.value;
    if (typeof wrapped !== "string") {
      throw new Error("wrapped_sync_key_missing");
    }
    const peerJwk = entry.public_key_jwk?.value;
    if (!peerJwk || typeof peerJwk !== "object") {
      throw new Error("device_public_key_missing");
    }
    return unwrapSyncKeyFromJwk(
      wrapped,
      this.deviceKeys!.x25519.privateKey,
      peerJwk as Parameters<typeof unwrapSyncKeyFromJwk>[2],
    );
  }

  verifyRemoteEnrollmentProof(proofB64: string, trustedSigningPublicKey: Uint8Array) {
    return verifyEnrollmentProof(trustedSigningPublicKey, proofB64);
  }

  verifyEnrollmentProofFromCrdt(proofB64: string): void {
    this.ensureDeviceKeys();
    const payload = JSON.parse(
      Buffer.from(proofB64, "base64").toString("utf8"),
    ) as { sync_device_id: string };

    const trustedDeviceId = Object.keys(this.crdtState.devices).find(
      (id) => id !== payload.sync_device_id && !isDeviceRevoked(this.crdtState.devices[id]),
    );
    if (!trustedDeviceId) {
      throw new Error("trusted_device_not_found");
    }

    const trustedEntry = this.crdtState.devices[trustedDeviceId];
    const signingPublicKeyB64 = trustedEntry.signing_public_key?.value;
    if (typeof signingPublicKeyB64 !== "string") {
      throw new Error("trusted_device_signing_key_missing");
    }

    const trustedSigningKey = Buffer.from(signingPublicKeyB64, "base64");
    this.verifyRemoteEnrollmentProof(proofB64, trustedSigningKey);
  }

  setPendingEnrollmentProof(proofB64: string): void {
    this.pendingEnrollmentProof = proofB64;
  }

  async adoptSyncKey(syncKey: Uint8Array, accountRootKey?: Uint8Array): Promise<void> {
    this.syncKey = syncKey;
    if (accountRootKey) {
      this.accountRootKey = accountRootKey;
    }
    await this.persistLocal();
  }

  async adoptDeviceEnrollment(deviceKeys: DeviceKeys, syncKey: Uint8Array): Promise<void> {
    this.deviceKeys = deviceKeys;
    this.syncKey = syncKey;
    this.crdtState = emptyCrdtState();
    this.localDirty = false;
    await this.persistLocal();
  }

  async restoreFromRecovery(
    passphrase: string,
    accountEmail: string,
    ciphertextB64: string,
    nonceB64: string,
    accountId: number | string,
  ): Promise<void> {
    const plaintext = decryptRecoveryBackup(passphrase, accountEmail, ciphertextB64, nonceB64);
    const accountRootKey = Buffer.from(plaintext.account_root_key, "base64");
    const syncKey = restoreSyncKeyFromBackup(plaintext, accountId);
    this.accountRootKey = accountRootKey;
    this.syncKey = syncKey;
    if (plaintext.crdt_snapshot) {
      this.crdtState = plaintext.crdt_snapshot;
    }
    this.localDirty = true;
    await this.persistLocal();
  }

  private async ensureSyncKey(remote: WalletSyncBlobResponse): Promise<void> {
    if (this.syncKey) {
      return;
    }
    if (!remote.ciphertext_base64) {
      throw new EnrollmentRequiredError();
    }
    this.syncKey = this.unwrapSyncKeyFromCrdt();
    await this.persistLocal();
  }

  private ensureDeviceKeys(): void {
    if (!this.deviceKeys) {
      throw new Error("wallet_not_initialized");
    }
  }

  private ensureInitialized(): void {
    if (!this.syncKey || !this.deviceKeys) {
      throw new Error("wallet_not_initialized");
    }
  }

  private async persistLocal(): Promise<void> {
    const { storage } = this.options;
    if (this.accountRootKey) {
      await storage.set(STORAGE_KEYS.accountRootKey, this.accountRootKey);
    }
    if (this.syncKey) {
      await storage.set(STORAGE_KEYS.syncKey, this.syncKey);
    }
    if (this.deviceKeys) {
      const serialized = serializeDeviceKeys(this.deviceKeys);
      await storage.set(
        STORAGE_KEYS.deviceKeys,
        new TextEncoder().encode(JSON.stringify(serialized)),
      );
    }
    await storage.set(
      STORAGE_KEYS.crdtState,
      new TextEncoder().encode(JSON.stringify(this.crdtState)),
    );
    await storage.set(
      STORAGE_KEYS.lastVersion,
      new TextEncoder().encode(String(this.lastKnownVersion)),
    );
  }
}

function serializeDeviceKeys(keys: DeviceKeys): Record<string, unknown> {
  return {
    syncDeviceId: keys.syncDeviceId,
    x25519: {
      publicKey: Buffer.from(keys.x25519.publicKey).toString("base64"),
      privateKey: Buffer.from(keys.x25519.privateKey).toString("base64"),
    },
    signingPrivateKey: Buffer.from(keys.signingPrivateKey).toString("base64"),
    signingPublicKey: Buffer.from(keys.signingPublicKey).toString("base64"),
  };
}

function deserializeDeviceKeys(raw: DeviceKeys): DeviceKeys {
  const anyRaw = raw as unknown as {
    syncDeviceId: string;
    x25519: { publicKey: string; privateKey: string };
    signingPrivateKey: string;
    signingPublicKey: string;
  };
  return {
    syncDeviceId: anyRaw.syncDeviceId,
    x25519: {
      publicKey: Buffer.from(anyRaw.x25519.publicKey, "base64"),
      privateKey: Buffer.from(anyRaw.x25519.privateKey, "base64"),
    },
    signingPrivateKey: Buffer.from(anyRaw.signingPrivateKey, "base64"),
    signingPublicKey: Buffer.from(anyRaw.signingPublicKey, "base64"),
  };
}

export type { SyncSchedulerHooks as SyncScheduler };
